// @ts-strict-ignore
import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import capitalize from 'lodash/capitalize';
import {
  Box, Icon, IconName, Label, Text, TextMask,
} from '@usealloy/component-library';
import type { ServiceCredentials, Action } from 'flow/store/engine/types';
import { useAppSelector } from '~/store/reduxHooks';
import { UserContext } from '~/dashboard/context';
import { selectFocusedAction } from '~/dashboard/views/workflows/store/redux/selectors';
import { ButtonWithMarginTop, ColumnBox, GrayText } from '../../styles';
import { OutlineContainer, StyledServiceCredentialsBanner } from '../styles';
import { ColumnBoxWithGrayLabel } from '../ColumnBoxWithGrayLabel';

import { CREDENTIAL_OWNER_OPTION_KEY } from '../../../constants';
import type { ServiceOption, ReadOnlyProp } from '../../types';
import type { ServiceCredentialsOnChangePayload } from './types';
import {
  ChangeOwnerCondition, DisplayCredentialsType,
  // ServiceCredentials as ServiceCredentialsResponse,
} from './types';
import ServiceCredentialsModal, { ServiceCredentialModalMode } from '~/dashboard/views/settings/services/ServiceCredentials';
import ServiceCredentialApplyVerifyModal, { ApplyVerifyMode } from '~/flow/action-options/ServiceCredentialApplyVerifyModal';
import {
  isIdField, isSecretField, updateOptionsByKey, updateServiceCredentialByCondition,
} from './helpers';

export interface ServiceCredentialsContainerProps extends ReadOnlyProp {
  credentials: ServiceCredentials;
  onChange: (updatedData: ServiceOption[]) => void;
  serviceAction: Action;
  serviceOptions: ServiceOption[];
}

export default function ServiceCredentialsContainer({
  credentials, onChange, serviceAction, serviceOptions, readOnly,
}: ServiceCredentialsContainerProps) {
  const { agent, permissions } = useContext(UserContext);
  const focusedAction = useAppSelector(selectFocusedAction);
  /**
   * serviceOptions -> workflow editor 2.0 compatible schema
   * serviceCredentials -> contains all information on credentials
   *    and specific schema for each service (ServiceCredential modal uses this)
   */
  // save points: values that will be part of the payload to update redux
  const [options, setOptions] = useState<ServiceOption[]>([]);
  const [serviceCredentials, setServiceCredentials] = useState(credentials);
  // inProgress: values that are updating but might not get saved
  const [inProgressOptions, setInProgressOptions] = useState<ServiceOption[]>([]);
  const [inProgressCredentials, setInProgressCredentials] = useState(credentials);

  useEffect(() => {
    if (serviceOptions) {
      setOptions(serviceOptions);
      setInProgressOptions(serviceOptions);
    }
  }, [
    serviceOptions, setOptions, setInProgressCredentials,
  ]);

  // for modals:
  const [credApplyVerifyModalMode, setCredApplyVerifyModalMode] = useState<ApplyVerifyMode | ''>('');
  const [credApplyVerifyModalOpen, setCredApplyVerifyModalOpen] = useState<boolean>(false);
  const [serviceCredentialModalOpen, setServiceCredentialModalOpen] = useState<boolean>(false);

  const handleServiceCredentialsChange = useCallback((event: Event) => {
    const targetData = event.target as ServiceCredentialsOnChangePayload;

    setInProgressCredentials(prevState => ({
      ...prevState,
      credentials: {
        ...prevState.credentials,
        [targetData.id]: targetData.value,
      },
    }));

    setInProgressOptions(prevState => updateOptionsByKey(
      prevState, targetData.id, targetData.value,
    ));
  }, []);

  const handleServiceCredentialsClose = () => {
    setServiceCredentialModalOpen(false);
    setInProgressCredentials(serviceCredentials);
    setInProgressOptions(options);
  };

  const handleCredentialsOwnerChange = useCallback((event: Event) => {
    const targetData = event.target as ServiceCredentialsOnChangePayload;

    // update verify modal to change owner view:
    if (targetData.value !== serviceCredentials.credential_owner) {
      setCredApplyVerifyModalMode(ApplyVerifyMode.CHANGE_OWNER);
    }

    // update service credentials:
    if (agent?.isAgent && targetData.value === 'customer') {
      setInProgressCredentials(
        prevState => updateServiceCredentialByCondition(
          ChangeOwnerCondition.CUSTOMER_AGENT_TO_CUSTOMER,
          prevState,
          targetData.value,
        ));
    } else if (targetData.value === null || targetData.value === '') {
      setInProgressCredentials(
        prevState => updateServiceCredentialByCondition(
          ChangeOwnerCondition.NO_OWNER,
          prevState,
          targetData.value,
        ));
    } else {
      setInProgressCredentials(
        prevState => updateServiceCredentialByCondition(
          ChangeOwnerCondition.DEFAULT,
          prevState,
          targetData.value,
        ));
    }

    // update in progress options:
    setInProgressOptions(prevState => updateOptionsByKey(
      prevState, CREDENTIAL_OWNER_OPTION_KEY, targetData.value, serviceCredentials,
    ));
  }, [agent, serviceCredentials]);

  const handleApplyServiceCredentialChanges = useCallback(() => {
    setServiceCredentials(inProgressCredentials);
    onChange(inProgressOptions);

    setCredApplyVerifyModalOpen(false);
    setCredApplyVerifyModalMode('');
  }, [inProgressOptions, inProgressCredentials, onChange]);

  const handleServiceCredentialsSave = useCallback(() => {
    if (credApplyVerifyModalMode) {
      setCredApplyVerifyModalOpen(true);
    } else {
      handleApplyServiceCredentialChanges();
    }

    setServiceCredentialModalOpen(false);
  }, [credApplyVerifyModalMode, handleApplyServiceCredentialChanges]);


  const displayCredentials = useMemo(() => {
    const displayByType = {
      owner: [], ids: [], secrets: [], settings: [],
    };

    options.forEach(option => {
      const spaceSeparatedLabel = option.key.split('_').join(' ');
      let displayType: DisplayCredentialsType = null;

      if (option.key === CREDENTIAL_OWNER_OPTION_KEY) {
        displayType = DisplayCredentialsType.OWNER;
      } else if (isSecretField(serviceCredentials, option.key)) {
        displayType = DisplayCredentialsType.SECRETS;
      } else if (isIdField(serviceCredentials, option.key)) {
        displayType = DisplayCredentialsType.IDS;
      } else {
        displayType = DisplayCredentialsType.SETTINGS;
      }

      const isCustomerAgentAndCredentialOwnedByAlloy = !agent?.isEmployee
        && focusedAction?.options?.credentialOwner?.value === 'alloy';
      const isOptionValueHidden = isCustomerAgentAndCredentialOwnedByAlloy
        && displayType !== DisplayCredentialsType.OWNER;

      let component: React.ReactNode = null;

      if (isOptionValueHidden) {
        component = (
          <ColumnBoxWithGrayLabel
            label={spaceSeparatedLabel}
            text="•••••••"
            key={option.publicId}
          />
        );
      } else {
        switch (displayType) {
          case DisplayCredentialsType.OWNER: {
            component = (
              <ColumnBoxWithGrayLabel
                key={option.publicId}
                label={spaceSeparatedLabel}
                text={capitalize(option.value)}
              />
            );
            break;
          }
          case DisplayCredentialsType.SECRETS: {
            component = (
              <ColumnBoxWithGrayLabel label={spaceSeparatedLabel} key={option.publicId}>
                <TextMask maskedText={'*'.repeat(option.value.length)}>
                  <Text>{option.value}</Text>
                </TextMask>
              </ColumnBoxWithGrayLabel>
            );
            break;
          }
          default: {
            component = (
              <ColumnBoxWithGrayLabel
                key={option.publicId}
                label={spaceSeparatedLabel}
                text={option.value}
              />
            );
          }
        }
      }

      displayByType[displayType].push(component);
    });
    return [
      ...displayByType.owner,
      ...displayByType.ids,
      ...displayByType.secrets,
      // excluding displayByType.settings from being shown in display mode
    ];
  }, [
    agent, focusedAction, options, serviceCredentials,
  ]);

  return (
    <>
      <ColumnBox data-dd-privacy="mask">
        <Label>Service Credentials</Label>
        <OutlineContainer alignItems="flex-start" direction="column" padding>
          {displayCredentials.length
            ? <Box>{displayCredentials}</Box>
            : <GrayText>using Alloy credentials</GrayText>}

          {!readOnly && (
            <ButtonWithMarginTop
              onClick={() => setServiceCredentialModalOpen(true)}
            >
              Override Credentials
            </ButtonWithMarginTop>
          )}

          <StyledServiceCredentialsBanner>
            <Icon icon={IconName.Info} />
            <Text>
              Changing credentials here will only affect the service in this Workflow.
              To edit environment-wide credentials, go to
              <a
                href="/v3/dashboard/settings/services"
                target="_blank"
                style={{ textTransform: 'none', fontWeight: 300, color: 'var(--cl-border-focus-ring)' }}
              >
                Service Settings.
              </a>
            </Text>
          </StyledServiceCredentialsBanner>
        </OutlineContainer>
      </ColumnBox>

      {!readOnly && (
        <>
          <ServiceCredentialsModal
            data-dd-privacy="mask"
            mode={ServiceCredentialModalMode.WORKFLOW_ACTION_OPTIONS}
            serviceName={serviceAction?.service.name}
            serviceCredentials={inProgressCredentials?.credentials}
            credentialFormat={serviceCredentials?.credential_format} // never updated by form
            credentialOwner={inProgressCredentials?.credential_owner}
            hasUpdatePermission={Boolean(permissions['UPDATE:CustomerServices:credentials'])}
            isOpen={serviceCredentialModalOpen}
            handleServiceCredentialsClose={handleServiceCredentialsClose}
            handleServiceCredentialsSave={handleServiceCredentialsSave}
            handleServiceCredentialsChange={handleServiceCredentialsChange}
            handleCredentialsOwnerChange={handleCredentialsOwnerChange}
          />
          <ServiceCredentialApplyVerifyModal
            mode={credApplyVerifyModalMode}
            isOpen={credApplyVerifyModalOpen}
            onProceedAction={() => handleApplyServiceCredentialChanges()}
            onClose={() => {
              setCredApplyVerifyModalOpen(false);
              setCredApplyVerifyModalMode('');
            }}
          />
        </>
      )}
    </>
  );
}
